// Pronouns — subject, object (direct/indirect), reflexive, prepositional, and
// the placement rules that trip learners up. A consolidated pronoun chart sits
// at the bottom as a quick reference.
import { renderGuideSections } from "../ui/guideSections.js";

export function pronounsView(app) {
  const chart = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Pronoun Reference Chart</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Person</th>
          <th class="py-2 px-3">Subject</th>
          <th class="py-2 px-3">Direct obj.</th>
          <th class="py-2 px-3">Indirect obj.</th>
          <th class="py-2 px-3">Reflexive</th>
          <th class="py-2 px-3">After prep.</th>
        </tr></thead>
        <tbody>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">I</td><td class="py-2 px-3">io</td><td class="py-2 px-3">mi</td><td class="py-2 px-3">mi</td><td class="py-2 px-3">mi</td><td class="py-2 px-3">me</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (inf.)</td><td class="py-2 px-3">tu</td><td class="py-2 px-3">ti</td><td class="py-2 px-3">ti</td><td class="py-2 px-3">ti</td><td class="py-2 px-3">te</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">he / it</td><td class="py-2 px-3">lui</td><td class="py-2 px-3">lo</td><td class="py-2 px-3">gli</td><td class="py-2 px-3">si</td><td class="py-2 px-3">lui</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">she / it</td><td class="py-2 px-3">lei</td><td class="py-2 px-3">la</td><td class="py-2 px-3">le</td><td class="py-2 px-3">si</td><td class="py-2 px-3">lei</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you (formal)</td><td class="py-2 px-3">Lei</td><td class="py-2 px-3">La</td><td class="py-2 px-3">Le</td><td class="py-2 px-3">si</td><td class="py-2 px-3">Lei</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">we</td><td class="py-2 px-3">noi</td><td class="py-2 px-3">ci</td><td class="py-2 px-3">ci</td><td class="py-2 px-3">ci</td><td class="py-2 px-3">noi</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">you all</td><td class="py-2 px-3">voi</td><td class="py-2 px-3">vi</td><td class="py-2 px-3">vi</td><td class="py-2 px-3">vi</td><td class="py-2 px-3">voi</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">they (m.)</td><td class="py-2 px-3">loro</td><td class="py-2 px-3">li</td><td class="py-2 px-3">gli / loro</td><td class="py-2 px-3">si</td><td class="py-2 px-3">loro</td></tr>
          <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">they (f.)</td><td class="py-2 px-3">loro</td><td class="py-2 px-3">le</td><td class="py-2 px-3">gli / loro</td><td class="py-2 px-3">si</td><td class="py-2 px-3">loro</td></tr>
          <tr><td class="py-2 pr-3 font-medium">you all (formal · Loro)</td><td class="py-2 px-3">Loro</td><td class="py-2 px-3">Li / Le</td><td class="py-2 px-3">Loro</td><td class="py-2 px-3">si</td><td class="py-2 px-3">Loro</td></tr>
        </tbody>
      </table></div>
      <p class="text-sage-500 text-xs mt-3">After a preposition, use the stressed forms <i>me, te, lui, lei, noi, voi, loro</i>: <i>con me</i> (with me), <i>per te</i> (for you), <i>senza di lui</i> (without him) — never <i>con io / con tu</i>.</p>
    </div>
  `;

  renderGuideSections(
    app,
    "Pronouns",
    "Pronouns stand in for nouns so you don't repeat them. Italian has a different pronoun for each grammatical role — subject, direct object, indirect object, reflexive, and the stressed form used after a preposition.",
    [
      {
        title: "1. Subject pronouns — often dropped",
        body: `
          <p class="text-sage-700 mb-3">The verb ending already shows who the subject is, so subject pronouns are usually <b>omitted</b>: <i>Parlo italiano</i> (not <i>Io parlo italiano</i>) unless you need them for emphasis or contrast.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Emphasis:</b> <i>Pago <b>io</b></i> — <i>I'll</i> pay (not you).</li>
            <li><b>Contrast:</b> <i>Lui lavora ma lei studia.</i></li>
            <li><b>tu vs Lei:</b> <i>tu</i> is informal, <i>Lei</i> is formal/respectful (and takes the <i>she</i> verb form, often capitalised in writing).</li>
          </ul>
        `,
      },
      {
        title: "2. Direct vs indirect object pronouns",
        body: `
          <p class="text-sage-700 mb-2">The <b>direct object</b> receives the action (<i>what?</i>); the <b>indirect object</b> is who benefits (<i>to/for whom?</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5 mb-2">
            <li><b>Direct:</b> <i>Vedo <u>il libro</u> → Lo vedo.</i> (I see it.)</li>
            <li><b>Indirect:</b> <i>Do il libro <u>a Maria</u> → Le do il libro.</i> (I give her the book.)</li>
          </ul>
          <p class="text-sage-700 text-sm">When both appear, indirect comes first and the <i>mi/ti/ci/vi</i> forms become <b>me/te/ce/ve</b>; <b>gli/le</b> fuse with the direct pronoun into one word <b>glielo / gliela / glieli / gliele</b>: <i>Le do il libro → <b>Glielo</b> do.</i></p>
        `,
      },
      {
        title: "3. Placement",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>Before</b> a conjugated verb: <i>Lo vedo. Non ti capisco.</i></li>
            <li><b>Attached</b> to an infinitive (drop its final -e) or a gerund: <i>Voglio vederlo / Sto vedendolo.</i></li>
            <li><b>Attached</b> to informal commands: <i>Dimmelo!</i> — but <b>before</b> negative and formal ones: <i>Non dirmelo · Mi dica.</i></li>
          </ul>
        `,
      },
      {
        title: "4. Reflexive & prepositional pronouns",
        body: `
          <p class="text-sage-700 mb-2"><b>Reflexive</b> pronouns show the subject acts on itself — used with reflexive verbs: <i>Mi alzo</i> (I get [myself] up), <i>si chiama</i> (he calls himself = his name is).</p>
          <p class="text-sage-700 text-sm"><b>Stressed (prepositional)</b> pronouns follow words like <i>a, di, per, senza, con</i>: <i>per <b>me</b>, senza di <b>te</b>, vicino a <b>noi</b></i>. Italian has no fused forms — it's simply <i>con me</i>, <i>con te</i>.</p>
        `,
      },
    ],
    "",
    chart
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
